import React, { useState } from 'react';
import {
  Flex,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { DateRangePicker, DatePickerValue } from './components/DateRangePicker';
import { UsageCard } from './components/UsageCard';
import { TotalRequestsIcon } from './components/UsageIcons';
import { useGetUsageMetricsQuery } from '../../gql/graphql';
import {
  datePickerToQueryInput,
  processSitecoreAIMetrics,
} from './utils/processUsageData';

const sitecoreAISectionTitle = 'SitecoreAI';

const Usage = () => {
  const [datePickerValue, setDatePickerValue] = useState<DatePickerValue>({
    view: 'Monthly',
    currentMonth: new Date(),
  });

  const queryInput = datePickerToQueryInput(datePickerValue);

  // Uncomment when SalesForce has added subscription entitlements
  // const { data, loading, error } = useGetUsageDataQuery({
  //   variables: {
  //     input: queryInput
  //   },
  //   // Only fetch if we have valid query input
  //   skip: !queryInput || Object.keys(queryInput).length === 0,
  //   errorPolicy: 'all'
  // });

  // placeholder usageMetrics endpoint until salesforce has added subscription entitlements
  const {
    data: metricsData,
    loading: metricsLoading,
    error: metricsError,
  } = useGetUsageMetricsQuery({
    variables: {
      input: queryInput,
    },
    skip: !queryInput || Object.keys(queryInput).length === 0,
    errorPolicy: 'all',
  });

  // const processedData = processUsageData(data);
  // const processedMetrics = processUsageMetrics(metricsData);
  const processedSitecoreAIMetrics = processSitecoreAIMetrics(metricsData);

  return (
    <Flex flexDir='column' minHeight='100%'>
      <Flex justifyContent='space-between' width='100%'>
        <Flex direction='column' alignSelf='start'>
          <Heading as='h1' data-testid='usage-heading' mb='8'>
            Usage
          </Heading>
          {/* <Text my='8' data-testid='usage-description'>
            Monitor usage patterns with flexible date filtering
          </Text> */}
        </Flex>
      </Flex>

      <VStack spacing={6} align='stretch'>
        <DateRangePicker
          value={datePickerValue}
          onChange={setDatePickerValue}
        />

        {/* Uncomment when SalesForce has added subscription entitlements */}
        {/* {error && (
          <Alert status="error" mb={2}>
            <AlertIcon />
            Unable to load usage data. Please try selecting a different date or refresh the page.
          </Alert>
        )} */}

        {metricsError && (
          <Alert status='warning' mb={2}>
            <AlertIcon />
            Unable to load metrics data: {metricsError.message}
          </Alert>
        )}

        <VStack align='stretch' spacing={8}>
          <VStack align='stretch' spacing={4} mb={8}>
            <Text fontSize='xl' fontWeight='bold' color='gray.800'>
              {sitecoreAISectionTitle}
            </Text>

            <SimpleGrid columns={{ base: 1, xl: 2, '2xl': 3 }} spacing={6}>
              <UsageCard
                icon={<TotalRequestsIcon />}
                title='Total visits'
                value={processedSitecoreAIMetrics.totalRequests}
                loading={metricsLoading}
              />
            </SimpleGrid>
          </VStack>

          {/* Edge requests section */}
          {/* Top Section: Usage filtered by selected date range */}
          {/* <VStack align="stretch" spacing={4} mb={8}>
              <Text fontSize="xl" fontWeight="bold" color="gray.800">
                Edge Requests
              </Text>
              
              <SimpleGrid columns={{ base: 1, xl: 2, '2xl': 3 }} spacing={6}>
                <UsageCard
                  icon={<TotalRequestsIcon />}
                  title="Total requests"
                  value={processedMetrics.totalRequests}
                  loading={metricsLoading}
                />
                
                <UsageCard
                  icon={<ContentRequestsIcon />}
                  title="Content requests"
                  summaryData={[
                    { label: 'Uncached', value: processedMetrics.contentRequests.uncached.value.toLocaleString() },
                    { label: 'Cached', value: processedMetrics.contentRequests.cached.value.toLocaleString() }
                  ]}
                  loading={metricsLoading}
                />
                
                <UsageCard
                  icon={<MediaRequestsIcon />}
                  title="Media requests"
                  summaryData={[
                    { label: 'Uncached', value: processedMetrics.mediaRequests.uncached.value.toLocaleString() },
                    { label: 'Cached', value: processedMetrics.mediaRequests.cached.value.toLocaleString() }
                  ]}
                  loading={metricsLoading}
                />
              </SimpleGrid>
            </VStack> */}

          {/* Bottom Section: Usage for subscription period */}
          {/* Uncomment when SalesForce has added subscription entitlements */}
          {/* <VStack align="stretch" spacing={4}>
              <HStack spacing={3} align="center">
                <Text fontSize="xl" fontWeight="bold" color="gray.800">
                  Usage for Subscription period
                </Text>
                <Text fontSize="lg" color="gray.600">
                  Feb 01 2025 - Jan 31 2026
                </Text>
              </HStack>
              
              <SimpleGrid columns={{ base: 1, lg: 2, xl: 3 }} spacing={6}>
                <UsageCard
                  icon={<TotalRequestsIcon />}
                  title="Total requests"
                  value={processedData.totalRequests}
                  loading={loading}
                />
                
                <UsageCard
                  icon={<ContentRequestsIcon />}
                  title="Content requests"
                  progressData={[
                    {
                      label: 'Uncached',
                      value: processedData.contentRequests.uncached.value,
                      total: processedData.contentRequests.uncached.total,
                      variant: 'primary'
                    },
                    {
                      label: 'Cached',
                      value: processedData.contentRequests.cached.value,
                      total: processedData.contentRequests.cached.total,
                      variant: 'secondary'
                    }
                  ]}
                  loading={loading}
                />
                
                <UsageCard
                  icon={<MediaRequestsIcon />}
                  title="Media requests"
                  progressData={[
                    {
                      label: 'Uncached',
                      value: processedData.mediaRequests.uncached.value,
                      total: processedData.mediaRequests.uncached.total,
                      variant: 'primary'
                    },
                    {
                      label: 'Cached',
                      value: processedData.mediaRequests.cached.value,
                      total: processedData.mediaRequests.cached.total,
                      variant: 'secondary'
                    }
                  ]}
                  loading={loading}
                />
              </SimpleGrid>
            </VStack> */}
        </VStack>
      </VStack>
    </Flex>
  );
};

export default Usage;
